<template>
  <canvas graph />
</template>

<script>
import { animate, commaDecimal, getOffsetTop } from "@/utils";

export default {
  name: "Graph",
  props: { info: { type: Object } },

  data() {
    return {
      vy: [],
      ctx: null,
      ew: 0,
      eh: 0,
      showValue: true,
      animated: false,
    };
  },

  watch: { sv: "checkDraw" },

  methods: {
    // -------- lifecycle helpers --------
    checkDraw() {
      if (this.animated) return;
      const sv = this.sv;
      const d = getOffsetTop(this.$el) - sv;
      const h = window.visualViewport.height;
      if (d + this.$el.offsetHeight < h * 1.1) {
        animate(0, 1, 60, this.draw);
        this.animated = true;
      }
    },

    resize() {
      this.init();
      this.draw(1);
    },

    init() {
      this.ew = this.$el.offsetWidth * 2;
      this.eh = this.$el.offsetHeight * 2;
      this.$el.width = this.ew;
      this.$el.height = this.eh;
    },

    // -------- main draw --------
    draw(r) {
      const info = this.info;
      if (!info || !this.ctx) return;

      // ----- breakpoints / colors -----
      const viewportWidth = window.visualViewport.width || window.innerWidth;
      const screenXl = viewportWidth >= 1600;
      const screenLg = viewportWidth >= 1024;
      const screenMd = viewportWidth >= 768;

      const color1 = "#03c75a";
      const color2 = "#0777ff";
      const fill = "#373D42";

      // ----- data -----
      const labels = Array.isArray(info.label) ? info.label : [];
      const vals1 = Array.isArray(info.val1) ? info.val1 : [];
      const vals2 = Array.isArray(info.val2) ? info.val2 : [];
      const hasVal2 = !!vals2.length;

      const isLine = info.type === "line";
      const isEnergy = info.id.includes("energy");
      const isEarth = info.id.includes("earth");

      const count = labels.length;
      if (!count) return;

      // ----- canvas & fonts -----
      const ew = this.ew;
      const eh = this.eh;
      const ctx = this.ctx;
      ctx.lineWidth = 2;

      const isLabelNum = !isNaN(Number(labels[0]));
      const maxValLen = Math.floor(Math.max(...vals1, ...vals2).toString().length);

      const bfs = (screenXl ? 24 : screenLg ? 14 : screenMd ? 12 : 10) * 2;
      const xfs =
        (screenXl ? (isLabelNum ? 20 : 16) : screenLg ? (isLabelNum ? 14 : 12) : screenMd ? 12 : isLabelNum ? 12 : 10) *
        2;
      const yfs = (screenXl ? 22 : screenMd || maxValLen < 4 ? 14 : 12) * 2;
      const bw = (screenXl ? 88 : screenMd ? 48 : 40) * 2;

      // ----- y scale / ticks -----
      let ticks = [];
      let visualMin;
      let visualMax;
      const splits = info.y_split || 1;

      // ticks 계산
      if (isEnergy) {
        visualMin = 0.8;
        visualMax = info.max;
        ticks = [1, 2];
      } else {
        visualMax = Math.max(1, Number(info.max || 0), ...vals1, ...vals2);
        const minTick = Number(info.min || 0);

        ticks = [0];
        if (minTick > 0 && minTick < visualMax) {
          ticks.push(minTick);
          const upperSplits = Math.max(1, splits - 1);
          for (let i = 1; i <= upperSplits; i++) {
            ticks.push(Math.round(minTick + ((visualMax - minTick) * i) / upperSplits));
          }
        } else {
          for (let i = 1; i <= splits; i++) {
            ticks.push(Math.round((visualMax * i) / splits));
          }
        }
        visualMin = 0;
      }

      this.vy = ticks.map((v) => commaDecimal(v));

      const mt = Math.floor(eh / (labels.length * (isLine ? 1 : 2)));
      const mb = xfs + 28;
      const vh = eh - mt - mb;

      const n = ticks.length;
      const gap = n > 1 ? vh / (n - 1) : 0;

      const toOffset = (val) => {
        let v = Number(val);
        if (isNaN(v)) v = visualMin;

        if (v < visualMin) v = visualMin;
        if (v > visualMax) v = visualMax;

        return vh * ((v - visualMin) / (visualMax - visualMin));
      };

      // ----- layout (keep ox as-is) -----
      let ox = screenXl ? 72 : screenLg ? 46 : screenMd ? (isEarth ? 28 : 40) : 28;

      if (viewportWidth < 768) {
        if (info.id === "greengas") ox = 36;
        if (isEnergy) ox = 22;
      }
      ox *= 2;

      const sidePaddingRatio = isLine ? 0 : 0.1;
      const leftMargin = Math.round(ew * sidePaddingRatio);
      const rightMargin = leftMargin + (screenLg ? ox : 0);

      let graphLeft = leftMargin + ox;
      let graphRight = ew - rightMargin;

      const symShift = (delta) => {
        graphLeft += delta;
        graphRight -= delta;
      };
      if (isLine) symShift(ox * 0.2);
      if (viewportWidth <= 768) symShift(ox / 10);
      if (viewportWidth <= 375 && isLine) symShift(-ox / 2);

      const graphWidth = Math.max(1, graphRight - graphLeft);
      const unit = graphWidth / (count * 2);

      // 그룹/바 중심
      const barGap = screenXl ? 15 : 5;
      const baseCenterXRaw = (i) => graphLeft + unit * (i * 2 + 1);
      const leftCenterXRaw = (i) => baseCenterXRaw(i) - (bw / 2 + barGap);
      const rightCenterXRaw = (i) => baseCenterXRaw(i) + (bw / 2 + barGap);

      // 바 그래프의 idx(3) 막대를 중심으로 이동
      let globalShift = 0;
      if (info.type === "bar" && count >= 3) {
        const idx = 2;
        const rect = this.$el.getBoundingClientRect();
        const cssW = this.$el.offsetWidth || rect.width || 1;
        const scaleX = this.ew / cssW;
        const targetCanvasX = Math.round((viewportWidth / 2 - rect.left) * scaleX);
        const thirdLeftNow = (hasVal2 ? leftCenterXRaw(idx) : baseCenterXRaw(idx)) - bw / 2;
        globalShift = targetCanvasX - thirdLeftNow;
      }

      const baseCenterX = (i) => baseCenterXRaw(i) + globalShift;
      const leftCenterX = (i) => leftCenterXRaw(i) + globalShift;
      const rightCenterX = (i) => rightCenterXRaw(i) + globalShift;

      const oy = vh + mt;

      // ----- clear -----
      ctx.clearRect(0, 0, ew, eh);

      const y1 = oy - toOffset(1);
      const y2 = oy - toOffset(2);

      // ----- axis -----
      ctx.textAlign = "right";
      ctx.font = `500 ${yfs}px Calibre`;
      ctx.fillStyle = "#A3A9B1";
      ctx.strokeStyle = "#4D5359";
      if (isEnergy) ctx.strokeStyle = "#D8D8D8";

      ctx.fillText(info.unit || "", ox, xfs);

      if (isEnergy) {
        ctx.font = `500 ${xfs}px Calibre`;
        // y tick: 1, 2만
        ticks.forEach((val) => {
          const yTick = oy - toOffset(val);
          ctx.fillText(val, ox, yTick - 12);

          // 왼쪽 y축 쪽 보조선
          ctx.beginPath();
          ctx.moveTo(0, yTick);
          ctx.lineTo(ox, yTick);
          ctx.stroke();
        });
        ctx.beginPath();
        ctx.moveTo(0, y1);
        ctx.lineTo(ox, y1);
        ctx.stroke();
      } else {
        for (let i = 1; i < n; i++) {
          const yTick = oy - gap * i;
          ctx.fillText(this.vy[i], ox, yTick - 12);
          ctx.beginPath();
          ctx.moveTo(0, yTick);
          ctx.lineTo(ox, yTick);
          ctx.stroke();
        }
        ctx.beginPath();
        ctx.moveTo(0, oy);
        ctx.lineTo(ew, oy);
        ctx.stroke();
      }

      // ----- x labels -----
      ctx.textAlign = "center";
      ctx.font = `500 ${xfs}px Calibre`;

      const labelY = oy + 8 + yfs;
      for (let i = 0; i < count; i++) {
        const labelX = !(isLine || vals2[i]) ? leftCenterX(i) : baseCenterX(i);
        ctx.fillText(labels[i], labelX, labelY);
      }

      // ----- line -----
      if (isLine) {
        const lw = screenLg ? (isEarth ? 8 : 4) : 3;
        const hts = [],
          hts2 = [];

        ctx.beginPath();
        ctx.strokeStyle = color1;
        ctx.lineWidth = lw;
        for (let i = 0; i < count; i++) {
          if (!Number(vals1[i])) continue;
          const x = baseCenterX(i);
          const yy = oy - Math.floor(toOffset(vals1[i]) * r);
          hts[i] = yy;
          if (i === 0) ctx.moveTo(x, yy);
          else ctx.lineTo(x, yy);
        }
        ctx.stroke();

        if (!hasVal2) {
          ctx.lineTo(baseCenterX(count - 1), oy);
          ctx.lineTo(baseCenterX(0), oy);
          ctx.closePath();
          ctx.fillStyle = fill;
          ctx.fill();
        }

        if (hasVal2) {
          ctx.beginPath();
          ctx.strokeStyle = color2;
          ctx.lineWidth = lw;
          for (let i = 0; i < count; i++) {
            if (!Number(vals2[i])) continue;
            const x = baseCenterX(i);
            const yy = oy - Math.floor(toOffset(vals2[i]) * r);
            hts2[i] = yy;
            if (i === 0) ctx.moveTo(x, yy);
            else ctx.lineTo(x, yy);
          }
          ctx.stroke();

          ctx.fillStyle = color2;
          for (let i = 0; i < count; i++) {
            const x = baseCenterX(i);
            ctx.beginPath();
            ctx.arc(x, hts2[i], 10, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        ctx.fillStyle = color1;
        for (let i = 0; i < count; i++) {
          const x = baseCenterX(i);
          ctx.beginPath();
          ctx.arc(x, hts[i], 10, 0, Math.PI * 2);
          ctx.fill();
        }

        if (isEnergy) {
          // 점선
          const dashVal = Number(info.line);
          const ratio = (dashVal - 1) / (2 - 1);
          const yDash = y1 + (y2 - y1) * ratio;

          ctx.save();
          ctx.setLineDash([6, 14]);
          ctx.strokeStyle = "#a9a9a9";
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(graphLeft, yDash);
          ctx.lineTo(graphRight, yDash);
          ctx.stroke();
          ctx.restore();

          // 1.1x 텍스트
          ctx.save();
          ctx.font = `500 ${yfs}px Calibre`;
          ctx.fillStyle = "black";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";

          const labelX = baseCenterX(count - 1);
          const labelY = yDash - 8;

          ctx.fillText("1.1X", labelX, labelY);
          ctx.restore();
        }
        return;
      }

      // ----- bar -----
      if (info.type === "bar") {
        const hts = [],
          hts2 = [];

        // bar1
        ctx.fillStyle = color1;
        for (let i = 0; i < count; i++) {
          const cx = hasVal2 ? leftCenterX(i) : baseCenterX(i);
          const h = Math.floor(toOffset(Number(vals1[i]) || 0) * r);
          const yTop = oy - h;
          hts[i] = yTop;
          ctx.fillRect(Math.floor(cx - bw / 2), yTop, bw, h);
        }

        // bar2
        if (hasVal2) {
          ctx.fillStyle = color2;
          for (let i = 0; i < count; i++) {
            const cx = rightCenterX(i);
            const h = Math.floor(toOffset(Number(vals2[i]) || 0) * r);
            const yTop = oy - h;
            hts2[i] = yTop;
            ctx.fillRect(Math.floor(cx - bw / 2), yTop, bw, h);
          }
        }

        // value labels
        if (this.showValue || r >= 1) {
          ctx.font = `500 ${bfs}px Calibre`;
          ctx.textAlign = "center";
          ctx.fillStyle = "#ffffff";
          const valYOffset = Math.floor(ew / 30);
          for (let i = 0; i < count; i++) {
            const cxL = hasVal2 ? leftCenterX(i) : baseCenterX(i);
            if (hasVal2) {
              const cxR = rightCenterX(i);
              ctx.fillText(commaDecimal(vals2[i]), cxR, hts2[i] + valYOffset);
            }
            ctx.fillText(commaDecimal(vals1[i]), cxL, hts[i] + valYOffset);
          }
        }
      }
    },
  },

  mounted() {
    if (!this.info) return;
    this.ctx = this.$el.getContext("2d");
    this.init();
    this.checkDraw();
    window.addEventListener("resize", this.resize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.resize);
  },
};
</script>

<style lang="less">
@import "~@/less/proj";
</style>
